// Google Analytics 4 integration — respects cookie consent (Senegal Loi 2008-12).
// GA4 is placed on the "functional" consent tier: page views and e-commerce
// measurement only; no cross-site advertising or session replay.
//
// Loading strategy: the gtag.js script is injected dynamically at runtime
// so the HTML stays clean and loading is conditional on VITE_GA4_ID being set.

import { isConsentGiven } from "@/lib/consent-manager";

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataLayer: any[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gtag: (...args: any[]) => void;
  }
}

const GA4_MEASUREMENT_ID = import.meta.env.VITE_GA4_ID as string | undefined;

let initialized = false;

// ─── Script injection ────────────────────────────────────────────────

function injectGtagScript(measurementId: string): void {
  if (document.getElementById("ga4-script")) return; // already injected

  const script = document.createElement("script");
  script.id = "ga4-script";
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);
}

// ─── gtag wrapper ────────────────────────────────────────────────────

function gtag(...args: unknown[]): void {
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push(args);
}

// ─── Public API ──────────────────────────────────────────────────────

/**
 * Initialize GA4. Safe to call multiple times — idempotent.
 * No-op when VITE_GA4_ID is not set or functional consent is absent.
 */
export function initGA4(): void {
  if (!GA4_MEASUREMENT_ID) return;
  if (!isConsentGiven("functional")) return;
  if (initialized) return;

  injectGtagScript(GA4_MEASUREMENT_ID);

  gtag("js", new Date());
  gtag("config", GA4_MEASUREMENT_ID, {
    // Respect Do Not Track signal
    anonymize_ip: true,
    // Do not send user_id automatically — set explicitly after consent
    send_page_view: true,
  });

  initialized = true;
}

/**
 * Disable GA4 data collection (e.g., when user revokes consent).
 * Sets the window property that gtag respects to stop further pings.
 */
export function shutdownGA4(): void {
  if (!GA4_MEASUREMENT_ID) return;

  // gtag opt-out property — prevents future hits from being sent
  const disableKey = `ga-disable-${GA4_MEASUREMENT_ID}`;
  (window as unknown as Record<string, boolean>)[disableKey] = true;
  initialized = false;
}

/**
 * Send a raw GA4 event. Silently skipped when GA4 is not initialized
 * or functional consent is absent.
 */
export function trackGA4Event(
  eventName: string,
  params?: Record<string, unknown>
): void {
  if (!initialized) return;
  if (!isConsentGiven("functional")) return;

  try {
    gtag("event", eventName, params ?? {});
  } catch {
    // Never let analytics break the app
  }
}

// ─── Standard e-commerce events ──────────────────────────────────────
// All prices are in FCFA (XOF). GA4 currency field is set accordingly.

export interface GA4ViewItemParams {
  readonly itemId: string;
  readonly itemName: string;
  readonly brand: string;
  readonly priceFcfa: number;
}

export interface GA4AddToCartParams {
  readonly itemId: string;
  readonly itemName: string;
  readonly brand: string;
  readonly quantity: number;
  readonly priceFcfa: number;
}

export interface GA4BeginCheckoutParams {
  readonly cartTotalFcfa: number;
  readonly itemCount: number;
}

export interface GA4PurchaseParams {
  readonly transactionId: string;
  readonly totalFcfa: number;
  readonly paymentMethod: string;
  readonly items?: ReadonlyArray<{
    readonly item_id: string;
    readonly item_name: string;
    readonly quantity: number;
    readonly price: number;
  }>;
}

export const ga4Ecommerce = {
  /**
   * GA4 standard event: view_item
   * Fired when a user views a product detail page.
   */
  viewItem(params: GA4ViewItemParams): void {
    trackGA4Event("view_item", {
      currency: "XOF",
      value: params.priceFcfa,
      items: [
        {
          item_id: params.itemId,
          item_name: params.itemName,
          item_brand: params.brand,
          price: params.priceFcfa,
          quantity: 1,
        },
      ],
    });
  },

  /**
   * GA4 standard event: add_to_cart
   * Fired when a user adds a product to their cart.
   */
  addToCart(params: GA4AddToCartParams): void {
    trackGA4Event("add_to_cart", {
      currency: "XOF",
      value: params.priceFcfa * params.quantity,
      items: [
        {
          item_id: params.itemId,
          item_name: params.itemName,
          item_brand: params.brand,
          price: params.priceFcfa,
          quantity: params.quantity,
        },
      ],
    });
  },

  /**
   * GA4 standard event: begin_checkout
   * Fired when a user enters the checkout flow.
   */
  beginCheckout(params: GA4BeginCheckoutParams): void {
    trackGA4Event("begin_checkout", {
      currency: "XOF",
      value: params.cartTotalFcfa,
      num_items: params.itemCount,
    });
  },

  /**
   * GA4 standard event: purchase
   * Fired when an order is confirmed (after payment success callback).
   */
  purchase(params: GA4PurchaseParams): void {
    trackGA4Event("purchase", {
      transaction_id: params.transactionId,
      currency: "XOF",
      value: params.totalFcfa,
      payment_type: params.paymentMethod,
      items: params.items ?? [],
    });
  },
} as const;

// ─── Provider factory ─────────────────────────────────────────────────
// Translates the internal trackEvent naming convention into GA4 event names.
// Register this via registerAnalyticsProvider() in main.tsx.

/** Maps internal event names (used by trackEvent) to GA4 event names. */
const EVENT_NAME_MAP: Readonly<Record<string, string>> = {
  product_viewed: "view_item",
  added_to_cart: "add_to_cart",
  checkout_started: "begin_checkout",
  order_completed: "purchase",
  // Pass-through: names already valid for GA4 custom events
};

/**
 * Creates a GA4 provider compatible with registerAnalyticsProvider().
 *
 * Usage in main.tsx:
 *   registerAnalyticsProvider(createGA4Provider());
 */
export function createGA4Provider(): (
  name: string,
  properties?: Record<string, unknown>
) => void {
  return (name: string, properties?: Record<string, unknown>) => {
    if (!initialized) return;

    const ga4Name = EVENT_NAME_MAP[name] ?? name;
    trackGA4Event(ga4Name, properties);
  };
}
